import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTheme } from "../hooks/useTheme";
import { NAV, isModuleActive, type NavEntry, type StudioModule } from "../modules";
import auraLogo from "../assets/aura-logo.png";

function SidebarItem({ module }: { module: StudioModule }) {
  const { pathname } = useLocation();

  const inner = (
    <>
      <span className="side-icon"><module.icon size={17} stroke={1.75} /></span>
      <span>{module.label}</span>
      {module.comingSoon && <span className="soon-badge">soon</span>}
    </>
  );

  return (
    <NavLink
      to={module.path}
      className={({ isActive }) =>
        `side-link${isActive || isModuleActive(module, pathname) ? " active" : ""}${module.comingSoon ? " soon" : ""}`
      }
    >
      {inner}
    </NavLink>
  );
}

function SidebarSection({ section }: { section: Extract<NavEntry, { kind: "section" }> }) {
  const { pathname } = useLocation();
  // Default expanded; a section with an active child always stays open.
  const [collapsed, setCollapsed] = useState(false);
  const childActive = section.children.some((c) => isModuleActive(c, pathname));
  const open = childActive || !collapsed;

  return (
    <div className="side-section">
      <button
        className={`side-section-head${open ? " open" : ""}`}
        onClick={() => setCollapsed(open)}
        aria-expanded={open}
      >
        <span className="side-caret">▸</span>
        <span>{section.label}</span>
      </button>
      <div className={`side-children${open ? "" : " closed"}`}>
        {section.children.map((m) => (
          <SidebarItem key={m.key} module={m} />
        ))}
      </div>
    </div>
  );
}

export function Layout() {
  const { user, authRequired, signOut } = useAuth();
  const { theme, toggle } = useTheme();

  return (
    <div className="app">
      <header className="app-header">
        <Link to="/" className="brand">
          <img className="brand-logo" src={auraLogo} alt="Aura" />
          <span className="brand-name">Aura Admin</span>
        </Link>
        <span className="brand-sub">build · customize · test · publish</span>
        <div className="spacer" />
        <button
          className="btn btn-ghost"
          onClick={toggle}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
        >
          {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
        </button>
        {authRequired && user && (
          <div className="user-box">
            <span className="muted">{user.email}</span>
            <button className="btn btn-ghost" onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        )}
      </header>
      <div className="app-body">
        <aside className="sidebar">
          {NAV.map((entry) =>
            entry.kind === "link" ? (
              <SidebarItem key={entry.module.key} module={entry.module} />
            ) : (
              <SidebarSection key={entry.key} section={entry} />
            ),
          )}
        </aside>
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
